import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, catchError, forkJoin, from, of, takeUntil } from 'rxjs';

import { VendaFolhaComponent } from '@shared/components/venda-folha/venda-folha.component';
import { VendaFolhaDadosStruct } from '@shared/components/venda-folha/structs/venda-folha-dados.struct';
import { VendaFolhaEmpresaStruct } from '@shared/components/venda-folha/structs/venda-folha-empresa.struct';
import { LayoutImpressaoVendaEnum } from '@shared/enums/vendas/layout-impressao-venda.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { EmpresaService } from '@shared/services/apis/usuario/empresa.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { VendaDocumentoService } from '@shared/services/apis/vendas/venda-documento.service';
import { EmpresaEtiquetaDadosService } from '@shared/services/etiqueta/empresa-etiqueta-dados.service';
import { ConfigOrcamento } from '@shared/services/models/vendas/config-orcamento.model';
import { ConfigPedidoVenda } from '@shared/services/models/vendas/config-pedido-venda.model';
import { RegistrarImpressaoVendaRequest } from '@shared/services/requests/vendas/registrar-impressao-venda.request';
import { ConfigFinancasStruct } from '@shared/services/structs/vendas/config-financas.struct';
import { FormaEnvioSelectStruct } from '@shared/services/structs/vendas/forma-envio-select.struct';
import { EmpresaStruct } from '@shared/services/structs/usuario/empresa.struct';
import { formatarNumeroDocumento } from '../../../processo-venda/processo-venda.models';

/** O que o documento entrega ao abrir a prévia: as configurações e os lookups que ele já carregou. */
export interface VdImpressaoDialogData {
    idVenda: number;
    idVendaTipoDocumento: number;
    nomeEtapa: string | null;
    configOrcamento: ConfigOrcamento | null;
    configPedidoVenda: ConfigPedidoVenda | null;
    listImpostoImpressaoOrcamento: number[];
    listImpostoImpressaoPedido: number[];
    financas: ConfigFinancasStruct | null;
    formasEnvio: FormaEnvioSelectStruct[];
}

/**
 * Prévia e impressão do documento comercial (§11). A folha é o `app-venda-folha` (a mesma da página pública do
 * link); aqui ficam o cabeçalho, o seletor de layout, o "Gerar PDF" e a ocorrência de impressão. A folha é relida
 * do orquestrador ao abrir (imprime o documento gravado, com a URL assinada da imagem de capa); configurações e
 * lookups vêm do documento, que já os carregou, e empresa e casas decimais da sessão.
 */
@Component({
    selector: 'app-vd-impressao-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        VendaFolhaComponent,
    ],
    templateUrl: './vd-impressao-dialog.component.html',
    styleUrls: ['./vd-impressao-dialog.component.css'],
})
export class VdImpressaoDialogComponent implements OnInit, OnDestroy {
    public readonly layouts: number[] = [
        LayoutImpressaoVendaEnum.Padrao,
        LayoutImpressaoVendaEnum.Detalhado,
        LayoutImpressaoVendaEnum.Recibo,
    ];

    public isCarregando = true;
    public isImprimindo = false;
    public layout: number = LayoutImpressaoVendaEnum.Padrao;
    public dados: VendaFolhaDadosStruct | null = null;

    @ViewChild(VendaFolhaComponent) private folha?: VendaFolhaComponent;

    private readonly destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<VdImpressaoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: VdImpressaoDialogData,
        private vendaDocumentoService: VendaDocumentoService,
        private processoVendaService: ProcessoVendaService,
        private empresaEtiquetaDadosService: EmpresaEtiquetaDadosService,
        private empresaService: EmpresaService,
        private authService: AuthService,
        private alertService: AlertService,
    ) {}

    public ngOnInit(): void {
        this.carregar();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private carregar(): void {
        const idEmpresa = this.authService.getUserInfos()?.idEmpresa;

        // Empresa degrada sem bloquear: sem ela a folha sai com o que a sessão tem (nome e CNPJ).
        forkJoin({
            impressao: this.vendaDocumentoService.getImpressao(this.data.idVenda),
            valoresEmpresa: from(this.empresaEtiquetaDadosService.getValoresEmpresa()).pipe(catchError(() => of(null))),
            empresa: this.empresaService.obterDadosPorId(idEmpresa).pipe(catchError(() => of(null))),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ impressao, valoresEmpresa, empresa }) => {
                    this.isCarregando = false;

                    if (impressao.isError || !impressao.venda) {
                        this.alertService.show('Aviso', impressao.errorDescription || 'Documento não encontrado.', AlertType.warning);
                        this.fechar();
                        return;
                    }

                    const usuario = this.authService.getUserInfos();
                    const venda = impressao.venda;

                    this.dados = {
                        documento: { ...venda, volumes: venda.volumes?.reduce((soma, volume) => soma + (volume.quantidade || 0), 0) || null },
                        cliente: impressao.cliente,
                        nomeVendedor: impressao.nomeVendedor,
                        nomeEtapa: this.data.nomeEtapa,
                        empresa: this.montarEmpresa(valoresEmpresa, empresa?.isError ? null : empresa?.empresa ?? null),
                        configOrcamento: this.data.configOrcamento,
                        configPedidoVenda: this.data.configPedidoVenda,
                        listImpostoImpressao: this.isPedido ? this.data.listImpostoImpressaoPedido : this.data.listImpostoImpressaoOrcamento,
                        financas: this.data.financas,
                        nomeFormaEnvio: this.data.formasEnvio.find((forma) => forma.idFormaEnvio === venda.idFormaEnvio)?.descricao || null,
                        emailEnviadoPor: usuario?.email || null,
                        casasDecimaisPreco: usuario?.casasDecimaisPreco || 2,
                        casasDecimaisQuantidade: usuario?.casasDecimaisQuantidade || 2,
                    };
                },
                error: () => {
                    this.isCarregando = false;
                    this.alertService.show('Erro', 'Não foi possível carregar o documento para impressão.', AlertType.error);
                    this.fechar();
                },
            });
    }

    private montarEmpresa(valores: Record<string, string> | null, empresa: EmpresaStruct | null): VendaFolhaEmpresaStruct {
        return {
            urlLogo: valores?.['empresa.logo'] || null,
            nomeEmpresa: valores?.['empresa.nome'] || this.authService.getUserInfos()?.nomeEmpresa || '',
            nomeFantasia: empresa?.nomeFantasia || null,
            cnpj: valores?.['empresa.cnpj'] || null,
            inscricaoEstadual: empresa?.inscricaoEstadual || null,
            cep: valores?.['empresa.cep'] || null,
            logradouro: valores?.['empresa.logradouro'] || null,
            numero: valores?.['empresa.numero'] || null,
            complemento: valores?.['empresa.complemento'] || null,
            bairro: valores?.['empresa.bairro'] || null,
            municipio: valores?.['empresa.cidade'] || null,
            uf: valores?.['empresa.uf'] || null,
        };
    }

    public get isPedido(): boolean {
        return this.data.idVendaTipoDocumento === VendaTipoDocumentoEnum.PedidoDeVenda;
    }

    public get tituloDocumento(): string {
        return this.isPedido ? 'Pedido de Venda' : 'Orçamento';
    }

    public get numeroDocumento(): string {
        return this.dados ? formatarNumeroDocumento(this.dados.documento.numero, this.dados.documento.idVendaTipoDocumento) : '';
    }

    // ---- Ações ----

    public alterarLayout(layout: number): void {
        this.layout = layout;
    }

    public rotuloLayout(layout: number): string {
        if (layout === LayoutImpressaoVendaEnum.Detalhado) return 'detalhado';
        if (layout === LayoutImpressaoVendaEnum.Recibo) return 'recibo';
        return 'padrão';
    }

    /** §11.1: `Ctrl+Enter` gera o PDF (o `Esc` já fecha pelo diálogo). */
    @HostListener('document:keydown', ['$event'])
    public aoTeclar(evento: KeyboardEvent): void {
        if ((evento.ctrlKey || evento.metaKey) && evento.key === 'Enter') {
            evento.preventDefault();
            this.imprimir();
        }
    }

    /**
     * Gerar PDF (§11.1): abre a janela de impressão do navegador, onde o usuário imprime ou salva em PDF, e grava a
     * ocorrência de impressão só com a janela aberta. A ocorrência não segura a folha: se a gravação falhar, avisa e
     * gera do mesmo jeito.
     */
    public imprimir(): void {
        if (!this.folha || this.isImprimindo) return;

        this.isImprimindo = true;
        this.folha.imprimir(() => this.registrarImpressao()).then((abriu) => {
            this.isImprimindo = false;

            if (!abriu) {
                this.alertService.show('Aviso', 'Libere as janelas pop-up do navegador para gerar o PDF.', AlertType.warning);
                return;
            }

            // §11.1: o alerta de conclusão é sempre "PDF gerado", com o layout escolhido; a prévia fecha.
            this.alertService.show('PDF gerado', `Layout ${this.rotuloLayout(this.isPedido ? this.layout : LayoutImpressaoVendaEnum.Padrao)}.`, AlertType.success);
            this.fechar();
        });
    }

    private registrarImpressao(): void {
        const request: RegistrarImpressaoVendaRequest = {
            layoutImpressaoVenda: this.isPedido ? this.layout : null,
        };

        this.processoVendaService.registrarImpressao(this.data.idVenda, request).subscribe({
            next: (response) => {
                if (response.isError) {
                    this.alertService.show('Aviso', 'A ocorrência de impressão não foi registrada.', AlertType.warning);
                }
            },
            error: () => this.alertService.show('Aviso', 'A ocorrência de impressão não foi registrada.', AlertType.warning),
        });
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
